import { endOfMonth, isBefore, startOfMonth } from 'date-fns';
import { useCallback, useReducer } from 'react';

import { DateRange, DateValue } from '../types';

export type MonthRangeControlOptions = {
	onChange: (value: DateRange) => void;
	value: DateRange;
};

type State = {
	mode: 'idle' | 'selectEnd';
	first: DateValue;
	last: DateValue;
};

type Action = { type: 'SelectStart'; value: DateValue } | { type: 'ConsiderEndDate'; value: Date } | { type: 'ClearHover' } | { type: 'Reset' };

const initialState: State = {
	mode: 'idle',
	first: null,
	last: null,
};

const createRange = (a: Date, b: Date): DateRange => {
	// Convert to start of month for consistent month selection
	const monthA = startOfMonth(a);
	const monthB = startOfMonth(b);

	// For the end date, use the last day of the month
	const endMonthA = endOfMonth(a);
	const endMonthB = endOfMonth(b);

	// Return range with start date as first day and end date as last day of month
	return isBefore(monthA, monthB) ? [monthA, endMonthB] : [monthB, endMonthA];
};

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'SelectStart':
			return {
				...state,
				mode: 'selectEnd',
				first: action.value,
				last: action.value,
			};

		case 'ConsiderEndDate':
			return {
				...state,
				last: startOfMonth(action.value),
			};

		case 'ClearHover':
			// When clearing hover, reset last to first if we're in selectEnd mode
			return state.mode === 'selectEnd'
				? {
						...state,
						last: state.first,
					}
				: state;

		case 'Reset':
			return initialState;

		default:
			return state;
	}
};

export const useMonthRangeControl = ({ onChange, value }: MonthRangeControlOptions) => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const selectMonth = useCallback(
		(month: DateValue) => {
			if (!month) {
				return;
			}

			const monthStart = startOfMonth(month);

			if (state.mode === 'idle') {
				// If we're idle, we're picking the first month in the range
				dispatch({ type: 'SelectStart', value: monthStart });
			} else if (state.mode === 'selectEnd') {
				// If we're selecting the second month in the range
				if (!state.first || !state.last) {
					throw new Error('Invalid range state');
				}

				// Pass the original month date to createRange so it can determine end of month
				onChange(createRange(state.first, month));
				dispatch({ type: 'Reset' });
			}
		},
		[onChange, state]
	);

	const hoverMonth = useCallback(
		(month: Date) => {
			if (state.mode === 'selectEnd') {
				dispatch({ type: 'ConsiderEndDate', value: month });
			}
		},
		[state]
	);

	const clearHover = useCallback(() => {
		dispatch({ type: 'ClearHover' });
	}, []);

	const [selectionStart, selectionEnd] = (() => {
		if (state.mode === 'idle') {
			return value;
		}

		if (!state.first || !state.last) {
			throw new Error('Invalid range state');
		}

		return createRange(state.first, state.last);
	})();

	return {
		selectionEnd,
		selectionStart,
		onHoverMonth: hoverMonth,
		onSelectMonth: selectMonth,
		onClearHover: clearHover,
		reset: () => dispatch({ type: 'Reset' }),
	};
};
